// ===========================================
// HOOKS: Usuarios Multi-Tenant
// ===========================================
//
// Estos hooks trabajan con el nuevo sistema multi-tenant.
// Reemplazan a useRoles.ts para la gestión de usuarios.
//

import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { auditoriaService } from '@/core/services/auditoriaService'
import { useNotificacionesModulo } from '@/core/hooks/useNotificacionesModulo'
import { PLANTILLAS_NOTIFICACION } from '@/core/types/notificaciones'
import {
  usuariosService,
  setAccessTokenGetter,
  type FiltrosUsuarios,
  type InvitarUsuarioParams,
} from '@/services/usuariosService'
import type { Rol, RolMultiTenant, Permisos } from '@/types/roles'

// ===========================================
// Hook para configurar el token de Auth0
// ===========================================

/**
 * Hook que configura el getter de token para el servicio de usuarios.
 * Debe usarse en un componente padre que tenga acceso a Auth0.
 */
export function useConfigureUsuariosAuth() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()

  useEffect(() => {
    if (isAuthenticated) {
      setAccessTokenGetter(async () => {
        return await getAccessTokenSilently()
      })
    }
  }, [isAuthenticated, getAccessTokenSilently])
}

// ===========================================
// Query Keys
// ===========================================

export const usuariosKeys = {
  all: ['usuarios-mt'] as const,
  lista: (orgId: string, filtros?: FiltrosUsuarios) =>
    [...usuariosKeys.all, 'lista', orgId, filtros] as const,
  detalle: (id: string) => [...usuariosKeys.all, 'detalle', id] as const,
  estadisticas: (orgId: string) =>
    [...usuariosKeys.all, 'estadisticas', orgId] as const,
  overridesRol: (orgId: string) =>
    [...usuariosKeys.all, 'overrides-rol', orgId] as const,
}

// ===========================================
// Hooks de Query
// ===========================================

/**
 * Lista todos los usuarios de la organización actual
 */
export function useUsuariosOrg(filtros?: FiltrosUsuarios) {
  const { organizacionId } = useOrganizacion()
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: usuariosKeys.lista(organizacionId || '', filtros),
    queryFn: () => {
      if (!organizacionId) throw new Error('No hay organización seleccionada')
      return usuariosService.listar(supabase!, organizacionId, filtros)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

/**
 * Obtiene un usuario por ID
 */
export function useUsuarioDetalle(usuarioId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: usuariosKeys.detalle(usuarioId),
    queryFn: () => usuariosService.obtenerPorId(supabase!, usuarioId),
    enabled: !!supabase && !supabaseLoading && !!usuarioId,
  })
}

/**
 * Obtiene estadísticas de usuarios de la organización
 */
export function useEstadisticasUsuariosOrg() {
  const { organizacionId } = useOrganizacion()
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: usuariosKeys.estadisticas(organizacionId || ''),
    queryFn: () => {
      if (!organizacionId) throw new Error('No hay organización seleccionada')
      return usuariosService.obtenerEstadisticas(supabase!, organizacionId)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}

// ===========================================
// Hooks de Mutation
// ===========================================

/**
 * Invitar un nuevo usuario
 */
export function useInvitarUsuario() {
  const queryClient = useQueryClient()
  const { organizacionId, usuario } = useOrganizacion()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { notificar, destinatariosOrg } = useNotificacionesModulo()

  return useMutation({
    mutationFn: (params: Omit<InvitarUsuarioParams, 'organizacionId'>) =>
      usuariosService.invitar({
        ...params,
        organizacionId: organizacionId || undefined,
      }),
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: usuariosKeys.all })

      // Registrar auditoría
      if (usuario?.email && supabase) {
        await auditoriaService.log(supabase, usuario.email, 'crear', 'usuario', data.user.id, {
          email_invitado: variables.email,
          rol: variables.role,
        })
      }

      // Notif Fase 5: usuario invitado → notificar a owners/admins de la org
      if (!organizacionId) return
      try {
        const actor = user?.name || user?.email || 'Alguien'
        const destinatarios = await destinatariosOrg(organizacionId, ['owner', 'admin'])
        if (destinatarios.length === 0) return
        const plantilla = PLANTILLAS_NOTIFICACION.usuarioInvitado(
          variables.email,
          variables.role,
          actor,
        )
        await notificar({
          ...plantilla,
          destinatarios,
          enlace: '/configuracion/usuarios',
        })
      } catch (err) {
        console.error('[notificaciones] error notificando usuario invitado:', err)
      }
    },
  })
}

/**
 * Enviar reset de password
 */
export function useResetPassword() {
  const { usuario } = useOrganizacion()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (params: { auth0UserId: string; email: string }) =>
      usuariosService.resetPassword(params.auth0UserId),
    onSuccess: async (_, variables) => {
      // Registrar auditoría
      if (usuario?.email && supabase) {
        await auditoriaService.log(
          supabase,
          usuario.email,
          'cambiar_estado',
          'usuario',
          undefined,
          {
            accion: 'reset_password',
            email_afectado: variables.email,
          }
        )
      }
    },
  })
}

/**
 * Bloquear/desbloquear usuario
 */
export function useToggleBloqueoUsuario() {
  const queryClient = useQueryClient()
  const { organizacionId, usuario } = useOrganizacion()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { notificar, destinatariosOrg } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({
      auth0UserId,
      block,
    }: {
      auth0UserId: string
      block: boolean
      email: string
    }) => usuariosService.toggleBloqueo(auth0UserId, block),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usuariosKeys.all })

      // Registrar auditoría
      if (usuario?.email && supabase) {
        await auditoriaService.log(
          supabase,
          usuario.email,
          'cambiar_estado',
          'usuario',
          undefined,
          {
            accion: variables.block ? 'bloquear' : 'desbloquear',
            email_afectado: variables.email,
          }
        )
      }

      // Notif Fase 5: usuario bloqueado/desbloqueado → afectado + owner/admin
      if (!organizacionId) return
      try {
        const actor = user?.name || user?.email || 'Alguien'
        const admins = await destinatariosOrg(organizacionId, ['owner', 'admin'])
        const destinatarios = [variables.email, ...admins]
        if (destinatarios.length === 0) return
        const plantilla = PLANTILLAS_NOTIFICACION.usuarioBloqueado(
          variables.email,
          variables.block,
          actor,
        )
        await notificar({
          ...plantilla,
          destinatarios,
          enlace: '/configuracion/usuarios',
        })
      } catch (err) {
        console.error('[notificaciones] error notificando bloqueo de usuario:', err)
      }
    },
  })
}

/**
 * Eliminar usuario
 */
export function useEliminarUsuarioOrg() {
  const queryClient = useQueryClient()
  const { organizacionId, usuario } = useOrganizacion()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { notificar, destinatariosOrg } = useNotificacionesModulo()

  return useMutation({
    mutationFn: async ({ auth0UserId, email }: { auth0UserId: string; email: string }) => {
      // Notif Fase 5: crear la notif ANTES del delete. Si el usuario está
      // logueado en otra pestaña recibe el toast por Realtime antes de perder
      // acceso. Los admins siempre la ven en su bandeja.
      if (organizacionId) {
        try {
          const actor = user?.name || user?.email || 'Alguien'
          const admins = await destinatariosOrg(organizacionId, ['owner', 'admin'])
          const destinatarios = [email, ...admins]
          if (destinatarios.length > 0) {
            const plantilla = PLANTILLAS_NOTIFICACION.usuarioEliminado(email, actor)
            await notificar({
              ...plantilla,
              destinatarios,
              enlace: '/configuracion/usuarios',
            })
          }
        } catch (err) {
          console.error('[notificaciones] error notificando eliminación de usuario:', err)
        }
      }

      return usuariosService.eliminar(auth0UserId)
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usuariosKeys.all })

      // Registrar auditoría
      if (usuario?.email && supabase) {
        await auditoriaService.log(supabase, usuario.email, 'eliminar', 'usuario', undefined, {
          email_eliminado: variables.email,
        })
      }
    },
  })
}

/**
 * Cambiar rol de usuario
 */
export function useCambiarRolUsuario() {
  const queryClient = useQueryClient()
  const { organizacionId } = useOrganizacion()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { notificar, destinatariosOrg } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({
      membresiaId,
      nuevoRol,
      updatedAt,
    }: {
      membresiaId: string
      nuevoRol: RolMultiTenant
      updatedAt: string
      email: string
      rolAnterior: RolMultiTenant
    }) => usuariosService.cambiarRol(supabase!, membresiaId, nuevoRol, updatedAt),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usuariosKeys.all })

      // Auditoría cubierta en-transacción por el RPC cambiar_rol_seguro (accion 'cambiar_rol').

      // Notif Fase 5: rol cambiado → afectado + owner/admin
      if (!organizacionId) return
      try {
        const actor = user?.name || user?.email || 'Alguien'
        const admins = await destinatariosOrg(organizacionId, ['owner', 'admin'])
        const destinatarios = [variables.email, ...admins]
        if (destinatarios.length === 0) return
        const plantilla = PLANTILLAS_NOTIFICACION.usuarioRolCambiado(
          variables.email,
          variables.rolAnterior,
          variables.nuevoRol,
          actor,
        )
        await notificar({
          ...plantilla,
          destinatarios,
          enlace: '/configuracion/usuarios',
        })
      } catch (err) {
        console.error('[notificaciones] error notificando cambio de rol:', err)
      }
    },
  })
}

/**
 * Actualizar workspaces de usuario
 */
export function useActualizarWorkspacesUsuario() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: ({
      membresiaId,
      workspaces,
      updatedAt,
    }: {
      membresiaId: string
      workspaces: string[]
      updatedAt: string
    }) => usuariosService.actualizarWorkspaces(supabase!, membresiaId, workspaces, updatedAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usuariosKeys.all })
    },
  })
}

/**
 * Actualizar permisos_extra / permisos_denegados de un usuario (override por-usuario).
 * El RPC valida gestionarUsuarios + denylist meta + lock optimista y audita en-tx.
 */
export function useActualizarPermisosUsuario() {
  const queryClient = useQueryClient()
  const { organizacionId } = useOrganizacion()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { notificar, destinatariosOrg } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({
      membresiaId,
      extra,
      denegados,
      updatedAt,
    }: {
      membresiaId: string
      extra: Partial<Permisos>
      denegados: (keyof Permisos)[]
      updatedAt: string
      email: string
    }) => usuariosService.actualizarPermisos(supabase!, membresiaId, extra, denegados, updatedAt),
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usuariosKeys.all })

      // Notif: permisos actualizados → afectado + owner/admin. El RPC ya audita en-tx.
      if (!organizacionId) return
      try {
        const actor = user?.name || user?.email || 'Alguien'
        const admins = await destinatariosOrg(organizacionId, ['owner', 'admin'])
        const destinatarios = [variables.email, ...admins]
        if (destinatarios.length === 0) return
        const plantilla = PLANTILLAS_NOTIFICACION.permisosActualizados(variables.email, actor)
        await notificar({
          ...plantilla,
          destinatarios,
          enlace: '/configuracion/usuarios',
        })
      } catch (err) {
        console.error('[notificaciones] error notificando permisos actualizados:', err)
      }
    },
  })
}

/**
 * Actualizar la política de permisos de un ROL para la organización (capa org-rol).
 * El RPC valida gestionarUsuarios + rol editable + sin flags meta y audita en-tx.
 */
export function useActualizarPermisosRol() {
  const queryClient = useQueryClient()
  const { organizacionId } = useOrganizacion()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: ({
      rol,
      cambios,
    }: {
      rol: Extract<Rol, 'contador_asociado' | 'asistente' | 'solo_lectura'>
      cambios: Record<string, boolean | null>
    }) => {
      if (!organizacionId) throw new Error('No hay organización seleccionada')
      return usuariosService.actualizarPermisosRol(supabase!, organizacionId, rol, cambios)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usuariosKeys.all })
      if (organizacionId) {
        queryClient.invalidateQueries({
          queryKey: usuariosKeys.overridesRol(organizacionId),
        })
      }
    },
  })
}

/**
 * Overrides org-rol vigentes de la organización (capa configurable por rol).
 */
export function useOverridesRol() {
  const { organizacionId } = useOrganizacion()
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: usuariosKeys.overridesRol(organizacionId || ''),
    queryFn: () => {
      if (!organizacionId) throw new Error('No hay organización seleccionada')
      return usuariosService.obtenerOverridesRol(supabase!, organizacionId)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
  })
}
